import { getValue } from 'firebase-utils'
import { EntityType, IEnrollmentEvent } from '../contexts/EnrollmentEventsContext'
import defaultCodeTranslations from './default-rejected-campaign-codes-translation'

type ConfigType = {
    by_code: {
        title?: string
        translatable_event_names?: string[],
        codes_translations?: Record<number | string, string>
    },
    by_event_name: Record<string, string>
}

const emptyConfig: ConfigType = {
    by_code: {},
    by_event_name: {}
}

const parseConfigValue = (value: string): ConfigType => {
    try {
        return JSON.parse(value)
    } catch {
        return emptyConfig
    }
}

let brandConfig = parseConfigValue(
    getValue(
        'sms_enrollment_brand_event_translations_config',
        (value) => {
            brandConfig = parseConfigValue(value)
        }
    )
)
let campaignConfig = parseConfigValue(
    getValue(
        'sms_enrollment_campaign_event_translations_config',
        (value) => {
            campaignConfig = parseConfigValue(value)
            campaignConfig.by_code.codes_translations = {
                ...defaultCodeTranslations,
                ...campaignConfig.by_code.codes_translations
            }
        }
    )
)
campaignConfig.by_code.codes_translations = {
    ...defaultCodeTranslations,
    ...campaignConfig.by_code.codes_translations
}

const findRejectionCodes = (text: string) => {
    // Matches "Rejection code 7103"
    // DCA declined sharing request for campaign C5ID7ST. Rejection category: PRIVACY_POLICY, CALL_TO_ACTION. Explanation: Rejection code 7103: Invalid privacy policy - The privacy policy does not mention that mobile opt-in willl not be shared, and appears to allow for the sharing of data with other companies. Rejection Code 2101: Invalid call to Action - no opt-in URL or attachment was found in th campaign submission.
    let codes: number[] = [...text.matchAll(/Rejection\s+Code\s+(\d+):/gi)]
        .map(match => Number(match[1]))
        .filter((x) => x)

    if (codes.length) return codes

    // Matches "(<code_number>)"
    // DCA2 declined sharing request for campaign CERDD5P. Rejection Category: MANDATORY_MESSAGE_TERMINOLOGY. Explanation: Unable to verify CTA information. (806)
    codes = [...text.matchAll(/\((\d+)\)/g)]
        .map(match => Number(match[1]))
        .filter((x) => x)

    return codes
}

const translateSingleEventByCode = (event: IEnrollmentEvent, config: ConfigType): IEnrollmentEvent | null => {
    const desc = event.description
    // example I got:
    // "DCA declined sharing request for campaign C5ID7ST. Rejection category: PRIVACY_POLICY, CALL_TO_ACTION.
    // Explanation: Rejection code 7103: Invalid privacy policy - The privacy policy does not mention that mobile opt-in willl not be shared,
    // and appears to allow for the sharing of data with other companies. Rejection Code 2120: Invalid call to Action - no opt-in URL
    // or attachment was found in th campaign submission."
    let translationCodes = findRejectionCodes(desc)
    if (!translationCodes.length) return null

    translationCodes = translationCodes
        .filter((x) => config.by_code.codes_translations?.[x.toString()])
    if (!translationCodes.length) return null

    const translatedItems = translationCodes
        .map((x) => config.by_code.codes_translations?.[x.toString()])
        .filter(Boolean)
        .map((translation) => `<li>${translation}</li>`)
        .join('')

    return {
        ...event,
        eventName: 'Rejection',
        description: `
            ${config.by_code.title || ''}
            <ul>
            ${translatedItems}
            </ul>
        `
    }
}

const translateByCode = (events: IEnrollmentEvent[], config: ConfigType) => {
    if (!config) return events
    if (!config?.by_code?.translatable_event_names?.length) return events
    const translatableEventNames = config?.by_code?.translatable_event_names
    const translated: IEnrollmentEvent[] = []

    const reversedArr = events.reverse()
    for (const event of reversedArr) {
        if (translatableEventNames.includes(event.eventName)) {
            const translatedEvent = translateSingleEventByCode(event, config)
            if (translatedEvent) translated.push(translatedEvent)
        }
        translated.push(event)
    }

    return translated
}

const translateSingleEventByEventName = (event: IEnrollmentEvent, config: ConfigType): IEnrollmentEvent | null => {
    const description = config.by_event_name[event.eventName]
    if (!description) return null
    return {
        ...event,
        eventName: 'Rejection',
        description
    }
}

const translateByEventName = (events: IEnrollmentEvent[], config: ConfigType) => {
    if (!config) return events
    if (!config?.by_event_name) return events
    const translated: IEnrollmentEvent[] = []

    const reversedArr = events.reverse()
    for (const event of reversedArr) {
        if (config.by_event_name[event.eventName]) {
            const translatedEvent = translateSingleEventByEventName(event, config)
            if (translatedEvent) translated.push(translatedEvent)
        }
        translated.push(event)
    }

    return translated
}

/**
 *
 */
const translate = (entityType: EntityType, events: IEnrollmentEvent[]): IEnrollmentEvent[] => {
    const config = entityType === 'campaign' ? campaignConfig : brandConfig
    return translateByCode(translateByEventName(events, config), config)
}

export default translate
